import { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Spinner,
  Stack,
  Table,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { LuArchive, LuArchiveRestore, LuPencil, LuPlus } from 'react-icons/lu';
import {
  archiveProject,
  listProjects,
  unarchiveProject,
  type Project,
  type ProjectStatus,
} from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { toaster } from '@/components/ui/toaster';

export function Projects() {
  const navigate = useNavigate();
  const { refresh: refreshActive } = useProject();
  const [tab, setTab] = useState<ProjectStatus>('active');
  const [rows, setRows] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      setRows(await listProjects(tab));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar la lista');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, [tab]);

  async function handleArchive(p: Project) {
    setBusyId(p.id);
    try {
      await archiveProject(p.id);
      toaster.create({ title: 'Proyecto archivado', description: p.name, type: 'success' });
      await refresh();
      await refreshActive();
    } catch (err) {
      toaster.create({
        title: 'No se pudo archivar',
        description: err instanceof Error ? err.message : '',
        type: 'error',
      });
    } finally {
      setBusyId(null);
    }
  }

  async function handleUnarchive(p: Project) {
    setBusyId(p.id);
    try {
      await unarchiveProject(p.id);
      toaster.create({ title: 'Proyecto restaurado', description: p.name, type: 'success' });
      await refresh();
      await refreshActive();
    } catch (err) {
      toaster.create({
        title: 'No se pudo restaurar',
        description: err instanceof Error ? err.message : '',
        type: 'error',
      });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
      <Stack gap="6" maxW="6xl">
        <Stack
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'flex-end' }}
          gap="4"
        >
          <Stack gap="1">
            <Text
              fontSize="xs"
              color="fg.muted"
              textTransform="uppercase"
              letterSpacing="0.06em"
              fontWeight="700"
            >
              Administración
            </Text>
            <Heading size={{ base: 'xl', md: '2xl' }} letterSpacing="-0.02em">
              Proyectos
            </Heading>
            <Text color="fg.muted" fontSize={{ base: 'sm', md: 'md' }}>
              Cada lotificación es un proyecto independiente con su propio inventario y cartera.
            </Text>
          </Stack>
          <Button
            colorPalette="brand"
            bg="brand.600"
            color="white"
            _hover={{ bg: 'brand.700' }}
            onClick={() => navigate('/proyectos/nuevo')}
            alignSelf={{ base: 'stretch', md: 'flex-end' }}
          >
            <Icon as={LuPlus} /> Nuevo proyecto
          </Button>
        </Stack>

        <Tabs.Root value={tab} onValueChange={(v) => setTab(v.value as ProjectStatus)}>
          <Tabs.List>
            <Tabs.Trigger value="active">Activos</Tabs.Trigger>
            <Tabs.Trigger value="archived">Archivados</Tabs.Trigger>
          </Tabs.List>

          <Box pt="6">
            {error && (
              <Text color="red.600" fontSize="sm" mb="4">
                {error}
              </Text>
            )}
            {loading ? (
              <HStack gap="3" color="fg.muted">
                <Spinner size="sm" />
                <Text fontSize="sm">Cargando…</Text>
              </HStack>
            ) : rows.length === 0 ? (
              <Box
                bg="bg.surface"
                borderWidth="1px"
                borderColor="border"
                rounded="lg"
                p="10"
                textAlign="center"
              >
                <Text color="fg.muted" fontSize="sm">
                  {tab === 'active'
                    ? 'Aún no hay proyectos activos. Crea el primero para empezar.'
                    : 'No hay proyectos archivados.'}
                </Text>
              </Box>
            ) : (
              <>
                {/* Mobile: card list */}
                <Stack gap="3" display={{ base: 'flex', md: 'none' }}>
                  {rows.map((p) => (
                    <Box
                      key={p.id}
                      bg="bg.surface"
                      borderWidth="1px"
                      borderColor="border"
                      rounded="lg"
                      p="4"
                    >
                      <Stack gap="3">
                        <Stack gap="1">
                          <HStack justify="space-between" align="flex-start" gap="3">
                            <Stack gap="0.5" minW={0}>
                              <Text fontSize="xs" color="fg.muted" fontWeight="700">
                                {p.code}
                              </Text>
                              <Text fontWeight="600" lineHeight="1.2">
                                {p.name}
                              </Text>
                            </Stack>
                            <Badge
                              colorPalette={p.status === 'active' ? 'green' : 'gray'}
                              flexShrink={0}
                            >
                              {p.status === 'active' ? 'Activo' : 'Archivado'}
                            </Badge>
                          </HStack>
                          <Text fontSize="sm" color="fg.muted">
                            {[p.municipalityName, p.departmentName].filter(Boolean).join(', ') ||
                              'Sin ubicación'}
                          </Text>
                        </Stack>
                        <HStack gap="2" wrap="wrap">
                          <Button size="xs" variant="outline" asChild disabled={busyId === p.id}>
                            <RouterLink to={`/proyectos/${p.id}`}>
                              <Icon as={LuPencil} /> Editar
                            </RouterLink>
                          </Button>
                          {p.status === 'active' ? (
                            <Button
                              size="xs"
                              variant="ghost"
                              colorPalette="gray"
                              onClick={() => handleArchive(p)}
                              loading={busyId === p.id}
                            >
                              <Icon as={LuArchive} /> Archivar
                            </Button>
                          ) : (
                            <Button
                              size="xs"
                              variant="ghost"
                              colorPalette="green"
                              onClick={() => handleUnarchive(p)}
                              loading={busyId === p.id}
                            >
                              <Icon as={LuArchiveRestore} /> Restaurar
                            </Button>
                          )}
                        </HStack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                {/* Desktop: table */}
                <Box
                  bg="bg.surface"
                  borderWidth="1px"
                  borderColor="border"
                  rounded="lg"
                  overflow="hidden"
                  display={{ base: 'none', md: 'block' }}
                >
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Código</Table.ColumnHeader>
                        <Table.ColumnHeader>Nombre</Table.ColumnHeader>
                        <Table.ColumnHeader>Ubicación</Table.ColumnHeader>
                        <Table.ColumnHeader>Estado</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign="right">Acciones</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {rows.map((p) => (
                        <Table.Row key={p.id}>
                          <Table.Cell fontWeight="600">{p.code}</Table.Cell>
                          <Table.Cell>{p.name}</Table.Cell>
                          <Table.Cell color="fg.muted">
                            {[p.municipalityName, p.departmentName].filter(Boolean).join(', ') ||
                              '—'}
                          </Table.Cell>
                          <Table.Cell>
                            <Badge colorPalette={p.status === 'active' ? 'green' : 'gray'}>
                              {p.status === 'active' ? 'Activo' : 'Archivado'}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <HStack justify="flex-end" gap="2">
                              <Button
                                size="xs"
                                variant="outline"
                                asChild
                                disabled={busyId === p.id}
                              >
                                <RouterLink to={`/proyectos/${p.id}`}>
                                  <Icon as={LuPencil} /> Editar
                                </RouterLink>
                              </Button>
                              {p.status === 'active' ? (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorPalette="gray"
                                  onClick={() => handleArchive(p)}
                                  loading={busyId === p.id}
                                >
                                  <Icon as={LuArchive} /> Archivar
                                </Button>
                              ) : (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorPalette="green"
                                  onClick={() => handleUnarchive(p)}
                                  loading={busyId === p.id}
                                >
                                  <Icon as={LuArchiveRestore} /> Restaurar
                                </Button>
                              )}
                            </HStack>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Box>
              </>
            )}
          </Box>
        </Tabs.Root>
      </Stack>
    </Box>
  );
}
